import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { firstValueFrom, take } from 'rxjs';

import { RoundStatus } from '@models/round-status.enum';
import { RoundPredictionStatusModel } from '@models/round-prediction-status.model';
import { RoundStatsSummaryModel } from '@models/round-stats-summary.model';
import { QuizResultDialogData } from '@models/quiz/quiz-result-dialog-data';
import { AuthService } from '@auth/services/auth.service';
import { PredictorRoundService } from '@predictor/services/predictor-round.service';
import { ModalResultComponent } from '@predictor/components/modal-result/modal-result.component';
import { ScreenSizeService } from '@services/screen-size.service';

@Injectable({ providedIn: 'root' })
export class ResultModalService {
  private readonly roundService = inject(PredictorRoundService);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly screenSizeService = inject(ScreenSizeService);

  private lastCheckedUserId: string | null = null;
  private readonly pendingRoundIds = new Set<string>();
  private isDialogOpen = false;

  private getStorageKey(roundUuid: string): string {
    return `result_modal_shown_${roundUuid}`;
  }

  private isStoredAsShown(roundUuid: string): boolean {
    return !!localStorage.getItem(this.getStorageKey(roundUuid));
  }

  private isInFlight(roundUuid: string): boolean {
    return this.pendingRoundIds.has(roundUuid);
  }

  /** Single entry point — opens the result modal at most once per round. */
  showResultModal(roundUuid: string, data: QuizResultDialogData): boolean {
    if (this.isDialogOpen || this.isStoredAsShown(roundUuid) || this.isInFlight(roundUuid)) {
      return false;
    }

    // Mark before opening so a page refresh cannot show the same round again
    localStorage.setItem(this.getStorageKey(roundUuid), 'true');
    this.pendingRoundIds.add(roundUuid);

    const openDialog = () => {
      if (this.isDialogOpen) {
        this.pendingRoundIds.delete(roundUuid);
        return;
      }

      try {
        this.isDialogOpen = true;

        const isMobile = this.screenSizeService.isMobile();
        const config: MatDialogConfig<QuizResultDialogData> = {
          width: '420px',
          autoFocus: false,
          disableClose: true,
          panelClass: 'quiz-dialog-panel',
          data,
          ...(isMobile
            ? { maxWidth: '95vw', maxHeight: '90dvh' }
            : { maxWidth: '90vw' }),
        };

        const dialogRef = this.dialog.open(ModalResultComponent, config);

        dialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe(() => {
            this.isDialogOpen = false;
            this.pendingRoundIds.delete(roundUuid);
          });
      } catch {
        localStorage.removeItem(this.getStorageKey(roundUuid));
        this.isDialogOpen = false;
        this.pendingRoundIds.delete(roundUuid);
      }
    };

    // Defer so callers (e.g. effects) don't open dialogs synchronously
    setTimeout(openDialog, 100);
    return true;
  }

  /** On login — show the latest resulted or completed round the user participated in. */
  async checkAndShowResultModal(): Promise<void> {
    const user = this.authService.user();
    if (!user?.userId) return;

    // Run once per authenticated user session
    if (this.lastCheckedUserId === user.userId) return;
    this.lastCheckedUserId = user.userId;

    try {
      const roundsResponse = await firstValueFrom(
        this.roundService.getPredictorRounds(1, 10),
      );

      const round = roundsResponse.items.find(
        (r) => r.status.name === RoundStatus.RESULTED,
      );
      if (!round || this.isStoredAsShown(round.uuid) || this.isInFlight(round.uuid)) {
        return;
      }

      const predStatus = await firstValueFrom(
        this.roundService.getPredictionStatus(round.uuid),
      ).catch(() => null) as RoundPredictionStatusModel | null;

      if (!predStatus || predStatus.answeredQuestions === 0) return;

      let stats: RoundStatsSummaryModel | null = null;
      try {
        stats = await firstValueFrom(
          this.roundService.getPredictorRoundStatsSummary(round.uuid),
        );
      } catch {
        stats = null;
      }

      const correct = stats?.correctPredictionsNumber ?? 0;
      const total = stats?.questionsNumber ?? predStatus.totalQuestions;
      const isWin = total > 0 && correct === total;

      this.showResultModal(round.uuid, {
        isWin,
        score: correct,
        total,
        header: round.name,
        prizeAmount: isWin ? round.prizeAmount : undefined,
      });
    } catch {
      // Silent fail — modal is non-critical
    }
  }
}
